import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Scale, 
  CreditCard, 
  FileText, 
  Users, 
  AlertTriangle, 
  Terminal, 
  Gavel, 
  ArrowLeft,
  ChevronRight,
  Globe
} from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('intro');

  // Scroll spy to update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 150) {
          current = section.getAttribute('id');
        }
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 120, // Offset for navbar
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  const LastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-blue-500 selection:text-white">
      
      {/* --- NAVBAR --- */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2">
                <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg" />
                <span className="font-bold text-lg text-white tracking-tight hidden md:block">Hollowpad</span>
             </div>
             <div className="h-6 w-px bg-slate-800 mx-2 hidden md:block"></div>
             <span className="font-medium text-slate-400 text-sm">Legal Center</span>
          </div>

          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition-all bg-slate-900 hover:bg-slate-800 px-4 py-2 rounded-full border border-slate-800"
          >
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Back to App</span>
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20">
        
        {/* Header */}
        <div className="mb-12 border-b border-slate-800 pb-8">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">Terms of Service</h1>
            <p className="text-lg text-slate-400 max-w-2xl">
                Please read these terms carefully before using the Hollowpad platform. 
                These terms govern your access to our AI generation tools, credit systems, and subscription services.
            </p>
            <p className="mt-4 text-sm text-slate-500 font-mono">
                Last Updated: <span className="text-slate-300">{LastUpdated}</span>
            </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* --- SIDEBAR NAVIGATION (Sticky) --- */}
            <aside className="hidden lg:block lg:col-span-3">
                <div className="sticky top-28 space-y-1">
                    <p className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Contents</p>
                    <nav className="space-y-1">
                        <NavLink id="intro" icon={FileText} label="1. Introduction" active={activeSection} onClick={scrollTo} />
                        <NavLink id="accounts" icon={Users} label="2. Account Registration" active={activeSection} onClick={scrollTo} />
                        <NavLink id="billing" icon={CreditCard} label="3. Subscriptions & Billing" active={activeSection} onClick={scrollTo} />
                        <NavLink id="credits" icon={Terminal} label="4. Virtual Credits Policy" active={activeSection} onClick={scrollTo} />
                        <NavLink id="refunds" icon={Scale} label="5. Refund Policy" active={activeSection} onClick={scrollTo} />
                        <NavLink id="license" icon={Shield} label="6. Intellectual Property" active={activeSection} onClick={scrollTo} />
                        <NavLink id="liability" icon={AlertTriangle} label="7. Disclaimers" active={activeSection} onClick={scrollTo} />
                        <NavLink id="general" icon={Gavel} label="8. General Provisions" active={activeSection} onClick={scrollTo} />
                    </nav>
                </div>
            </aside>

            {/* --- MAIN CONTENT --- */}
            <main className="lg:col-span-9 space-y-16">
                
                {/* 1. Introduction */}
                <Section id="intro" title="1. Introduction">
                    <p>
                        Welcome to <strong>Hollowpad</strong> ("we," "our," or "us"). By accessing or using our website, 
                        API, and software services (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). 
                        If you do not agree to these Terms, do not use the Service.
                    </p>
                    <p>
                        Hollowpad provides AI-powered tools designed to assist in the development of assets for the Roblox platform. 
                        <strong>We are not affiliated with, endorsed by, or sponsored by Roblox Corporation.</strong>
                    </p>
                </Section>

                {/* 2. Accounts */}
                <Section id="accounts" title="2. Account Registration">
                    <ul className="list-disc pl-5 space-y-2 text-slate-400">
                        <li>
                            <strong>Eligibility:</strong> You must be at least 13 years old to use the Service. 
                            If you are under 18, you represent that you have your parent or guardian's permission to use the Service.
                        </li>
                        <li>
                            <strong>Security:</strong> You are responsible for maintaining the confidentiality of your account credentials 
                            (managed via Firebase Authentication) and for all activities that occur under your account.
                        </li>
                        <li>
                            <strong>Accuracy:</strong> You agree to provide accurate, current, and complete information during the registration process.
                        </li>
                    </ul>
                </Section>

                {/* 3. Subscriptions */}
                <Section id="billing" title="3. Subscriptions & Billing">
                    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-6">
                        <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                            <CreditCard size={18} className="text-blue-400"/> Payment Processing
                        </h4>
                        <p className="text-sm">
                            Payments are processed securely via <strong>Paystack</strong>. 
                            While our plans may be denominated in USD for reference, charges will be processed in <strong>Nigerian Naira (NGN)</strong> 
                            at the prevailing exchange rate defined by our system.
                        </p>
                    </div>
                    <p>
                        <strong>3.1 Recurring Billing:</strong> By selecting a subscription plan (e.g., Basic Pass, Lightning Pass), 
                        you authorize us to charge your payment method on a recurring basis (monthly or weekly) until you cancel.
                    </p>
                    <p>
                        <strong>3.2 Upgrades/Downgrades:</strong> You may change your plan at any time via the "Settings" page. 
                        Upgrades take effect immediately; downgrades take effect at the end of the current billing cycle.
                    </p>
                    <p>
                        <strong>3.3 Cancellation:</strong> You may cancel your subscription at any time. 
                        Access to premium features will continue until the end of the current billing period.
                    </p>
                </Section>

                {/* 4. Credits Policy */}
                <Section id="credits" title="4. Virtual Credits Policy">
                    <p>
                        The Service uses a virtual credit system to meter usage of AI generation tools.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 my-4">
                        <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
                            <span className="text-slate-200 font-bold block mb-1">Basic Credits</span>
                            <span className="text-xs text-slate-500">
                                Used for standard generation tasks, text completion, and basic code snippets.
                            </span>
                        </div>
                        <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
                            <span className="text-cyan-300 font-bold block mb-1">Diamond Credits</span>
                            <span className="text-xs text-slate-500">
                                Used for high-complexity reasoning models, project management tasks, and priority generation.
                            </span>
                        </div>
                    </div>
                    <p>
                        <strong>4.1 Rollover Policy:</strong> Unlike many services, Hollowpad allows unused credits to roll over to the next billing cycle, 
                        <strong>provided that you maintain an active subscription</strong>. If your subscription expires or is cancelled, 
                        accrued credits may be frozen or forfeited after a grace period of 30 days.
                    </p>
                    <p>
                        <strong>4.2 No Monetary Value:</strong> Credits are a license to use the software and have no real-world monetary value. 
                        They cannot be transferred to other users or redeemed for cash.
                    </p>
                </Section>

                {/* 5. Refund Policy */}
                <Section id="refunds" title="5. Refund Policy">
                    <p>
                        We strive for satisfaction, but due to the digital nature of our AI services and the immediate costs incurred 
                        during generation, refunds are limited.
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-slate-400 mt-4">
                        <li>
                            <strong>Technical Errors:</strong> If you are charged due to a technical error (e.g., double billing), 
                            we will issue a full refund immediately upon verification.
                        </li>
                        <li>
                            <strong>Usage-Based:</strong> Refunds generally cannot be issued if you have utilized a significant portion 
                            of your credits during the billing cycle.
                        </li>
                        <li>
                            <strong>Procedure:</strong> To request a refund, contact support with your Transaction Reference ID. 
                            Requests must be made within 7 days of the transaction.
                        </li>
                    </ul>
                </Section>

                {/* 6. Intellectual Property */}
                <Section id="license" title="6. Intellectual Property & Commercial Rights">
                    <p>
                        <strong>6.1 Platform Rights:</strong> Hollowpad retains all rights to the underlying software, algorithms, and interface.
                    </p>
                    <p>
                        <strong>6.2 Generated Content:</strong>
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-slate-400 mt-2">
                        <li>
                            <strong>Free Tier:</strong> Assets generated on the free tier are for personal, non-commercial use only.
                        </li>
                        <li>
                            <strong>Premium Tiers (Basic/Lightning):</strong> Upon payment, you are granted a perpetual, worldwide, 
                            royalty-free license to use assets generated during your subscription for commercial purposes 
                            (including within Roblox experiences that generate Robux).
                        </li>
                    </ul>
                </Section>

                {/* 7. Liability */}
                <Section id="liability" title="7. Disclaimers & Limitation of Liability">
                    <div className="p-4 bg-red-900/10 border border-red-900/30 rounded-lg text-slate-300 text-sm">
                        <strong className="text-red-400 block mb-2 uppercase text-xs tracking-wider">Disclaimer of Warranties</strong>
                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." WE MAKE NO WARRANTIES REGARDING THE QUALITY, ACCURACY, 
                        OR RELIABILITY OF AI-GENERATED CODE OR ASSETS. USE THEM AT YOUR OWN RISK.
                    </div>
                    <p className="mt-4">
                        Hollowpad shall not be liable for any indirect, incidental, or consequential damages resulting from your use of the Service, 
                        including but not limited to loss of data, loss of Roblox account standing, or loss of revenue.
                    </p>
                    <p>
                        <strong>Roblox Compliance:</strong> You are solely responsible for ensuring that any content you generate and upload to Roblox 
                        complies with the Roblox Terms of Use and Community Standards. We are not responsible for moderation actions taken against your Roblox account.
                    </p>
                </Section>

                {/* 8. General */}
                <Section id="general" title="8. General Provisions">
                    <p>
                        <strong>8.1 Modifications:</strong> We reserve the right to modify these Terms at any time. 
                        We will notify users of significant changes via email or dashboard notification.
                    </p>
                    <p>
                        <strong>8.2 Termination:</strong> We reserve the right to suspend or terminate your account if you violate these Terms, 
                        abuse the API, or engage in illegal activity using our platform.
                    </p>
                    <p>
                        <strong>8.3 Governing Law:</strong> These terms shall be governed by the laws of the Federal Republic of Nigeria, 
                        without regard to its conflict of law provisions.
                    </p>
                </Section>

                {/* Contact Footer */}
                <div className="pt-12 border-t border-slate-800">
                    <h3 className="text-white font-bold mb-2">Have Questions?</h3>
                    <p className="text-slate-400 mb-4">
                        If you have any questions about these Terms, please contact us.
                    </p>
                    <button className="text-blue-400 hover:text-blue-300 font-medium flex items-center gap-2 transition-colors">
                        <Globe size={16} /> 
	  publicstring.care@gmail.com
                    </button>
                </div>

            </main>
        </div>
      </div>
    </div>
  );
}

// --- SUB COMPONENTS ---

function Section({ id, title, children }) {
    return (
        <section id={id} className="scroll-mt-32 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                {title}
            </h2>
            <div className="space-y-4 text-slate-400 leading-relaxed text-base">
                {children}
            </div>
        </section>
    );
}

function NavLink({ id, icon: Icon, label, active, onClick }) {
    const isActive = active === id;
    return (
        <button
            onClick={() => onClick(id)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive 
                ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20' 
                : 'text-slate-400 hover:bg-slate-900 hover:text-white border border-transparent'
            }`}
        >
            <div className="flex items-center gap-3">
                <Icon size={16} className={isActive ? 'text-blue-400' : 'text-slate-500'} />
                {label}
            </div>
            {isActive && <ChevronRight size={14} className="animate-in slide-in-from-left-2" />}
        </button>
    );
}
